import React from "react";
import { formatDistanceToNow } from "date-fns";

/**
 * SyncStatusBadge - Shows Plaid sync status with spinner if syncing
 *
 * @param {Date|string} lastSync - Last sync timestamp
 * @param {boolean} isSyncing - Whether currently syncing
 * @param {string} size - Size: 'sm', 'md', 'lg' (default: 'md')
 */
const SyncStatusBadge = ({ lastSync, isSyncing = false, size = "md" }) => {
  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-2.5 py-1",
    lg: "text-base px-3 py-1.5",
  };

  const iconSizes = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  if (isSyncing) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 ${sizeClasses[size]}`}
      >
        <svg
          className={`animate-spin ${iconSizes[size]}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span>Syncing...</span>
      </span>
    );
  }

  if (!lastSync) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ${sizeClasses[size]}`}
      >
        <svg
          className={iconSizes[size]}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>Not synced</span>
      </span>
    );
  }

  const lastSyncDate = typeof lastSync === "string" ? new Date(lastSync) : lastSync;
  const timeAgo = formatDistanceToNow(lastSyncDate, { addSuffix: true });

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 ${sizeClasses[size]}`}
      title={`Last synced: ${lastSyncDate.toLocaleString()}`}
    >
      <svg
        className={iconSizes[size]}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>Synced {timeAgo}</span>
    </span>
  );
};

export default SyncStatusBadge;
